import { useCallback, useEffect, useState } from "react";
import type { Shipment } from "api";
import { shipmentsApi } from "api";
import { isForbidden, isUnauthenticated } from "utils/error.utils";
import type { LoadStatus } from "./useProfile";

type UseShipments = {
  shipments: Shipment[];
  status: LoadStatus;
  removeMany: (ids: number[]) => Promise<void>;
  add: (shipment: Shipment) => void;
  /**
   * Fetch again in the background. Cards belong to the company, so a colleague
   * can have followed one since this list was loaded.
   */
  reload: () => void;
};

/**
 * Shipments saved from the tracking widget. They belong to the company, so a
 * colleague's card shows up here too — `created_by` says whose it was.
 *
 * An empty list is a legitimate answer, not an error: it drives the
 * "nothing tracked yet" state.
 */
export const useShipments = (onSessionLost: () => void): UseShipments => {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");

  const load = useCallback(
    (background: boolean) => {
      let cancelled = false;

      shipmentsApi
        .list()
        .then(({ data }) => {
          if (cancelled) return;
          setShipments(data.cards);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (isUnauthenticated(error)) {
            onSessionLost();
            setStatus("unauthorized");
            return;
          }
          // A refresh that fails leaves the list that is on screen alone: a blip
          // is not worth replacing readable cards with an error.
          if (background) return;

          setStatus(isForbidden(error) ? "unverified" : "failed");
        });

      return () => {
        cancelled = true;
      };
    },
    [onSessionLost]
  );

  useEffect(() => load(false), [load]);

  const reload = useCallback(() => {
    load(true);
  }, [load]);

  /**
   * Bulk delete, one request per card because that is what the back office
   * offers. `allSettled` keeps one failure from stranding the rest: whatever
   * went through leaves the list, and the rest stay for another try.
   */
  const removeMany = useCallback(
    async (ids: number[]) => {
      const results = await Promise.allSettled(ids.map((id) => shipmentsApi.remove(id)));
      const removed = new Set(ids.filter((_, index) => results[index].status === "fulfilled"));

      if (removed.size > 0) {
        setShipments((current) => current.filter((shipment) => !removed.has(shipment.id)));
      }

      const failure = results.find((result) => result.status === "rejected");

      if (failure === undefined) return;

      if (isUnauthenticated(failure.reason)) {
        onSessionLost();
        setStatus("unauthorized");
        return;
      }

      setStatus("failed");
    },
    [onSessionLost]
  );

  /**
   * A freshly followed card. Following an already-tracked shipment updates it
   * rather than duplicating it, so the existing row is replaced in place.
   */
  const add = useCallback((shipment: Shipment) => {
    setShipments((current) => [shipment, ...current.filter((item) => item.id !== shipment.id)]);
  }, []);

  return { shipments, status, removeMany, add, reload };
};
